import { Effect, Option, pipe } from "effect";
import { NativeRewards } from "./types";
import { getProtocolConfig } from "@silo/config";
import {
  Address,
  getChainKeyFromProtocol,
  ProtocolKeyService,
  USD_ZERO,
} from "@silo/types";
import { isNullable } from "effect/Predicate";
import { KvClient } from "../kv-client";
import { ContractsClient } from "../contracts-client";
import { convertToUsd } from "@silo/utils";

export type GetNativeRewards = ReturnType<typeof getNativeRewards>;
export type GetNativeRewardsSuccess = Effect.Effect.Success<GetNativeRewards>;
export type GetNativeRewardsError = Effect.Effect.Error<GetNativeRewards>;
export type GetNativeRewardsContext = Effect.Effect.Context<GetNativeRewards>;

export function getNativeRewards(user: Address) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const protocolConfig = getProtocolConfig(protocolKey);

    if (isNullable(protocolConfig.nativeRewards)) {
      return null;
    }

    const chainKey = getChainKeyFromProtocol(protocolKey);
    const { tokenAddress, symbol, decimals } = protocolConfig.nativeRewards;

    const [tokenPriceMap, balance] = yield* Effect.all(
      [
        KvClient.getTokenPriceMap([{ chainKey, tokenAddress }]),
        ContractsClient.getNativeRewardsBalance(user),
      ],
      { concurrency: "unbounded" }
    );

    const rewardsTokenPriceUsd = pipe(
      tokenPriceMap.get({ chainKey, tokenAddress }),
      Option.getOrElse(() => USD_ZERO)
    );

    const balanceUsd = convertToUsd(balance, decimals, rewardsTokenPriceUsd);

    const nativeRewards: NativeRewards = {
      rewardTokenAddress: tokenAddress,
      rewardTokenSymbol: symbol,
      rewardTokenDecimals: decimals,
      balance,
      balanceUsd,
    };

    return nativeRewards;
  });
}
